import { useState, useEffect } from 'react'
import { useAdminAuth } from '@/auth/useAdminAuth'
import { api } from '@/api/client'
import { useToastStore } from '@/store/toast'

interface Props {
  open: boolean
  initialTab?: 'profile' | 'security' | 'preferences'
  onClose: () => void
}

function getInitials(profile: any): string {
  const given = profile?.given_name || ''
  const family = profile?.family_name || ''
  if (given && family) return `${given[0]}${family[0]}`.toUpperCase()
  const email = profile?.email || ''
  return email ? email[0].toUpperCase() : '?'
}

function getFullName(profile: any): string {
  const parts = [profile?.given_name, profile?.family_name].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : profile?.email?.split('@')[0] || 'Admin'
}

const PASSWORD_RULES = [
  { key: 'length', label: '8+ characters', test: (v: string) => v.length >= 8 },
  { key: 'upper', label: 'Uppercase letter', test: (v: string) => /[A-Z]/.test(v) },
  { key: 'lower', label: 'Lowercase letter', test: (v: string) => /[a-z]/.test(v) },
  { key: 'number', label: 'Number', test: (v: string) => /[0-9]/.test(v) },
  { key: 'special', label: 'Special character (!@#$%)', test: (v: string) => /[^A-Za-z0-9]/.test(v) },
]

function passwordStrength(pw: string): { level: number; label: string; color: string } {
  const passed = PASSWORD_RULES.filter(r => r.test(pw)).length
  if (passed <= 1) return { level: 1, label: 'Weak', color: 'bg-red-500' }
  if (passed <= 2) return { level: 2, label: 'Fair', color: 'bg-orange-500' }
  if (passed <= 3) return { level: 3, label: 'Good', color: 'bg-yellow-500' }
  if (passed <= 4) return { level: 4, label: 'Strong', color: 'bg-green-500' }
  return { level: 5, label: 'Excellent', color: 'bg-green-600' }
}

export function AccountDrawer({ open, initialTab = 'profile', onClose }: Props) {
  const { user } = useAdminAuth()
  const toast = useToastStore()
  const profile = user?.profile as any

  const [tab, setTab] = useState(initialTab)
  useEffect(() => { if (open) setTab(initialTab) }, [open, initialTab])

  // Profile state
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [title, setTitle] = useState('')
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileLoaded, setProfileLoaded] = useState(false)

  // Security state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [passwordSaving, setPasswordSaving] = useState(false)
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Preferences state
  const [sidebarDefault, setSidebarDefault] = useState(() => localStorage.getItem('sidebar-default') || 'expanded')
  const [prefsSaving, setPrefsSaving] = useState(false)

  // Load profile on open
  useEffect(() => {
    if (!open || profileLoaded) return
    setProfileLoading(true)
    api.get<any>('/profile').then(data => {
      setFirstName(data.firstName || profile?.given_name || '')
      setLastName(data.lastName || profile?.family_name || '')
      setPhone(data.phone || '')
      setTitle(data.professional?.title || '')
      setProfileLoaded(true)
    }).catch(() => {
      setFirstName(profile?.given_name || '')
      setLastName(profile?.family_name || '')
      setProfileLoaded(true)
    }).finally(() => setProfileLoading(false))
  }, [open, profileLoaded])

  // Reset on close
  useEffect(() => {
    if (!open) {
      setPasswordMsg(null)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setProfileLoaded(false)
    }
  }, [open])

  async function saveProfile() {
    setProfileSaving(true)
    try {
      await api.patch('/profile', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        professional: title.trim() ? { title: title.trim() } : undefined,
      })
      toast.show('success', 'Profile updated')
    } catch {
      toast.show('error', 'Failed to update profile')
    } finally {
      setProfileSaving(false)
    }
  }

  async function changePassword() {
    setPasswordMsg(null)
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'Passwords do not match' })
      return
    }
    const failed = PASSWORD_RULES.filter(r => !r.test(newPassword))
    if (failed.length > 0) {
      setPasswordMsg({ type: 'error', text: `Password must have: ${failed.map(r => r.label).join(', ')}` })
      return
    }
    setPasswordSaving(true)
    try {
      await api.post('/auth/change-password', {
        currentPassword: currentPassword.trim(),
        newPassword: newPassword.trim(),
      })
      setPasswordMsg({ type: 'success', text: 'Password changed successfully!' })
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: any) {
      const msg = err?.body ? JSON.parse(err.body)?.error : 'Failed to change password'
      setPasswordMsg({ type: 'error', text: msg })
    } finally {
      setPasswordSaving(false)
    }
  }

  function savePreferences() {
    setPrefsSaving(true)
    localStorage.setItem('sidebar-default', sidebarDefault)
    setTimeout(() => {
      setPrefsSaving(false)
      toast.show('success', 'Preferences saved')
    }, 300)
  }

  if (!open) return null

  const initials = getInitials(profile)
  const fullName = getFullName(profile)
  const email = profile?.email || ''
  const strength = passwordStrength(newPassword)

  const tabs = [
    { key: 'profile', label: 'Profile' },
    { key: 'security', label: 'Security' },
    { key: 'preferences', label: 'Preferences' },
  ] as const

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div className="relative w-full max-w-[480px] bg-white shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">My Account</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400">
            <i className="pi pi-times" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                tab === t.key
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-slate-500 hover:text-slate-700'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* ─── PROFILE TAB ─── */}
          {tab === 'profile' && (
            <>
              {/* Identity card */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center text-white text-lg font-bold shrink-0">
                  {initials}
                </div>
                <div className="min-w-0">
                  <p className="text-base font-semibold text-slate-800 truncate">{fullName}</p>
                  <p className="text-sm text-slate-500 truncate">{email}</p>
                  <span className="inline-block mt-1 text-[10px] px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                    platform-operator
                  </span>
                </div>
              </div>

              {profileLoading ? (
                <div className="text-center py-8"><i className="pi pi-spin pi-spinner text-slate-400" /></div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">First Name</label>
                      <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Last Name</label>
                      <input type="text" value={lastName} onChange={e => setLastName(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Email</label>
                    <input type="text" value={email} readOnly
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-400 cursor-not-allowed" />
                    <p className="text-[10px] text-slate-400 mt-1">Email is managed by your identity provider</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Phone</label>
                    <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                      placeholder="+1 514 ..."
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Title / Role</label>
                    <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                      placeholder="e.g., Director, Professor, Student"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                  </div>
                  <button onClick={saveProfile} disabled={profileSaving}
                    className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                    {profileSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </>
          )}

          {/* ─── SECURITY TAB ─── */}
          {tab === 'security' && (
            <>
              <div>
                <h3 className="text-sm font-semibold text-slate-800 mb-4">Change Password</h3>
                <div className="space-y-3">
                  {/* Current password */}
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Current Password</label>
                    <div className="relative">
                      <input type={showCurrent ? 'text' : 'password'} value={currentPassword}
                        onChange={e => setCurrentPassword(e.target.value)}
                        className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg text-sm" />
                      <button type="button" onClick={() => setShowCurrent(!showCurrent)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        <i className={`pi ${showCurrent ? 'pi-eye-slash' : 'pi-eye'} text-sm`} />
                      </button>
                    </div>
                  </div>
                  {/* New password */}
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">New Password</label>
                    <div className="relative">
                      <input type={showNew ? 'text' : 'password'} value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg text-sm" />
                      <button type="button" onClick={() => setShowNew(!showNew)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        <i className={`pi ${showNew ? 'pi-eye-slash' : 'pi-eye'} text-sm`} />
                      </button>
                    </div>
                  </div>
                  {/* Confirm password */}
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Confirm New Password</label>
                    <div className="relative">
                      <input type={showConfirm ? 'text' : 'password'} value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg text-sm" />
                      <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        <i className={`pi ${showConfirm ? 'pi-eye-slash' : 'pi-eye'} text-sm`} />
                      </button>
                    </div>
                  </div>

                  {/* Strength meter */}
                  {newPassword && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${strength.color}`}
                            style={{ width: `${(strength.level / 5) * 100}%` }} />
                        </div>
                        <span className="text-xs font-medium text-slate-500">{strength.label}</span>
                      </div>
                      <div className="space-y-1">
                        {PASSWORD_RULES.map(rule => (
                          <div key={rule.key} className="flex items-center gap-2 text-xs">
                            <i className={`pi ${rule.test(newPassword) ? 'pi-check text-green-500' : 'pi-times text-slate-300'}`} />
                            <span className={rule.test(newPassword) ? 'text-green-700' : 'text-slate-400'}>{rule.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Confirm mismatch */}
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-red-500">Passwords do not match</p>
                  )}

                  {/* Message */}
                  {passwordMsg && (
                    <div className={`p-3 rounded-lg text-sm ${
                      passwordMsg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}>
                      <i className={`pi ${passwordMsg.type === 'success' ? 'pi-check-circle' : 'pi-exclamation-circle'} mr-2`} />
                      {passwordMsg.text}
                    </div>
                  )}

                  <button onClick={changePassword}
                    disabled={passwordSaving || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                    className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                    {passwordSaving ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ─── PREFERENCES TAB ─── */}
          {tab === 'preferences' && (
            <>
              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-2">Default Sidebar</label>
                  <div className="flex gap-3">
                    {(['expanded', 'compact'] as const).map(opt => (
                      <label key={opt} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="sidebar" value={opt}
                          checked={sidebarDefault === opt} onChange={() => setSidebarDefault(opt)}
                          className="accent-blue-600" />
                        <span className="text-sm text-slate-600 capitalize">{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <button onClick={savePreferences} disabled={prefsSaving}
                  className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                  {prefsSaving ? 'Saving...' : 'Save Preferences'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.2s ease-out;
        }
      `}</style>
    </div>
  )
}
